import { Routes, Route, Navigate } from 'react-router-dom'
import { LoginPage } from './modules/login'
import { RegisterPage } from './modules/register'
import { OAuthCallbackPage } from './modules/oauth-callback'
import {
  MfaRecoveryRequestPage,
  MfaRecoveryConfirmPage,
  MfaRecoveryCancelPage,
} from './modules/mfa-recovery'
import {
  TermsPage,
  PrivacyPage,
  HabeasDataPage,
  CookiesPage,
} from './modules/legal'
import { DashboardPage } from './modules/dashboard'
import { ProfilePage } from './modules/profile'
import { SelectOrgPage } from './modules/select-org'
import { AccountHubPage } from './modules/account'
import { PdfTemplatesPage } from './modules/pdf-templates'
import { PdfTemplateEditorPage } from './modules/pdf-templates/editor'
import { EmailTemplatesPage } from './modules/email-templates'
import { EmailTemplateEditorPage } from './modules/email-templates/editor'
import { EmailLogPage } from './modules/email-log'
import { PqrsfPage } from './modules/pqrsf'
import { PqrsfDetailPage } from './modules/pqrsf/detail'
import { AppShell } from './components/layout/app-shell'
import { hasValidAccessToken } from './lib/api'
import { ROUTES } from './lib/constants'
import { needsOrgSelection } from './lib/org-selection'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  // Hard gate: requires a JWT-shaped token whose `exp` is in the future.
  // The server is the source of truth (rejects via 401 on next call),
  // but this prevents the dashboard from even rendering when no token,
  // malformed token, or expired token is in memory.
  if (!hasValidAccessToken()) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }
  // Multi-org: si el flujo de login marcó que el usuario tiene >1
  // empresa y aún no eligió, lo desviamos a /select-org. Esto cierra
  // el bypass por refresh / typing manual de URL.
  if (needsOrgSelection()) {
    return <Navigate to={ROUTES.SELECT_ORG} replace />
  }
  return <AppShell>{children}</AppShell>
}

function SelectOrgRoute({ children }: { children: React.ReactNode }) {
  // /select-org requiere autenticación pero NO requiere haber elegido
  // org (es justamente donde se elige).
  if (!hasValidAccessToken()) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }
  return <>{children}</>
}

function AccountHubRoute({ children }: { children: React.ReactNode }) {
  // /account es el panel de identidad de Taxor (replica patrón Lex Account).
  // Requiere autenticación pero NO requiere haber elegido org — al contrario,
  // es el lugar visible para gestionar perfil/sesiones/empresas/MFA con
  // independencia de qué empresa esté activa en la sesión actual. Por eso
  // tampoco usa AppShell (no queremos el sidebar de operación aquí).
  if (!hasValidAccessToken()) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }
  return <>{children}</>
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="mx-auto max-w-4xl">
      <p className="font-mono text-xs uppercase tracking-wider text-fg-muted">▸ Skeleton Fase 1</p>
      <h1 className="mt-2 text-2xl font-bold text-fg-primary">{title}</h1>
      <p className="mt-3 text-sm text-fg-secondary">
        Esta sección se construye en su fase correspondiente. Ver{' '}
        <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-xs">
          md/998_features.md
        </code>{' '}
        para roadmap.
      </p>
    </div>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
      <Route path={ROUTES.OAUTH_CALLBACK} element={<OAuthCallbackPage />} />
      <Route path={ROUTES.MFA_RECOVERY_REQUEST} element={<MfaRecoveryRequestPage />} />
      <Route path={ROUTES.MFA_RECOVERY_CONFIRM} element={<MfaRecoveryConfirmPage />} />
      <Route path={ROUTES.MFA_RECOVERY_CANCEL} element={<MfaRecoveryCancelPage />} />
      <Route path="/legal/terminos" element={<TermsPage />} />
      <Route path="/legal/privacidad" element={<PrivacyPage />} />
      <Route path="/legal/habeas-data" element={<HabeasDataPage />} />
      {/* Alias retrocompatible para /legal/aviso-privacidad → habeas-data
          (mientras el SEO o algún email viejo aún apunten a la URL anterior). */}
      <Route path="/legal/aviso-privacidad" element={<HabeasDataPage />} />
      <Route path="/legal/cookies" element={<CookiesPage />} />
      <Route
        path={ROUTES.SELECT_ORG}
        element={
          <SelectOrgRoute>
            <SelectOrgPage />
          </SelectOrgRoute>
        }
      />
      <Route
        path={ROUTES.DASHBOARD}
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TAX_CLIENTS}
        element={
          <ProtectedRoute>
            <PlaceholderPage title="Tax-Clients (gestión ERPs)" />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.DOCUMENTS}
        element={
          <ProtectedRoute>
            <PlaceholderPage title="Documents (operación global)" />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AUDIT}
        element={
          <ProtectedRoute>
            <PlaceholderPage title="Audit Log" />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.BILLING}
        element={
          <ProtectedRoute>
            <PlaceholderPage title="Billing — revenue + facturas mensuales" />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SET_PRUEBAS}
        element={
          <ProtectedRoute>
            <PlaceholderPage title="Set de pruebas DIAN — habilitación PT" />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PDF_TEMPLATES}
        element={
          <ProtectedRoute>
            <PdfTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PDF_TEMPLATE_EDITOR}
        element={
          <ProtectedRoute>
            <PdfTemplateEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EMAIL_TEMPLATES}
        element={
          <ProtectedRoute>
            <EmailTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EMAIL_TEMPLATE_EDITOR}
        element={
          <ProtectedRoute>
            <EmailTemplateEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EMAIL_LOG}
        element={
          <ProtectedRoute>
            <EmailLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PQRSF}
        element={
          <ProtectedRoute>
            <PqrsfPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PQRSF_DETAIL}
        element={
          <ProtectedRoute>
            <PqrsfDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SETTINGS}
        element={
          <ProtectedRoute>
            <PlaceholderPage title="Settings — signing keys, planes, cert DIAN" />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROFILE}
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ACCOUNT}
        element={
          <AccountHubRoute>
            <AccountHubPage />
          </AccountHubRoute>
        }
      />
      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  )
}
